import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Snackbar, useTheme } from '@mui/material';
import { ContentCopy as CopyIcon, Check as CheckIcon } from '@mui/icons-material';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { vs2015 } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';

const CodeBlock = ({ language, code, showLineNumbers = true, maxHeight = null }) => {
  const [copied, setCopied] = useState(false);
  const theme = useTheme();
  
  // Choose style based on theme mode
  const codeStyle = theme.palette.mode === 'dark' ? a11yDark : vs2015;

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box 
      sx={{ 
        position: 'relative', 
        mb: 3,
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: theme.palette.mode === 'dark' 
          ? '0 4px 20px rgba(0,0,0,0.5)' 
          : '0 4px 20px rgba(0,0,0,0.1)'
      }}
    >
      <Box 
        sx={{ 
          position: 'absolute', 
          top: 8, 
          right: 8, 
          zIndex: 1,
          bgcolor: theme.palette.mode === 'dark' 
            ? 'rgba(255,255,255,0.1)' 
            : 'rgba(0,0,0,0.6)',
          borderRadius: 1,
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: theme.palette.mode === 'dark' 
              ? 'rgba(255,255,255,0.2)' 
              : 'rgba(0,0,0,0.8)',
          }
        }}
      >
        <Tooltip title={copied ? "Copied!" : "Copy code"}>
          <IconButton 
            size="small" 
            onClick={handleCopy}
            sx={{ 
              color: copied 
                ? theme.palette.success.main 
                : theme.palette.mode === 'dark' ? 'grey.300' : 'grey.100',
              transition: 'all 0.2s ease'
            }}
          >
            {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
      <SyntaxHighlighter 
        language={language} 
        style={codeStyle} 
        showLineNumbers={showLineNumbers}
        customStyle={{ 
          borderRadius: '8px',
          maxHeight: maxHeight || '500px',
          overflow: maxHeight ? 'auto' : 'auto',
          fontSize: '0.9rem',
          padding: '1.5rem',
          margin: 0
        }}
        lineNumberStyle={{
          color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'
        }}
      >
        {code}
      </SyntaxHighlighter>
      <Snackbar
        open={copied}
        autoHideDuration={2000}
        message="Code copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            bgcolor: theme.palette.mode === 'dark' ? 'success.dark' : 'success.light',
            color: theme.palette.mode === 'dark' ? 'white' : 'success.dark'
          }
        }}
      />
    </Box>
  );
};

export default CodeBlock;